import { useCallback, useEffect, useState } from "react";
import { TeamsAPI } from "../../APIs/teams.api";
import { UserNotificationsAPI } from "../../APIs/user_notifications.api";
import { useToast } from "../../components/toast";
import CrudTable from "../../components/crud-table";
import { ConfirmationModal } from "../../components/delete-confirmation-modal";
import { useTableControls } from "../../hooks/useTableControls";
import { CrudTableHeader } from "../../components/crud-table-header";
import { BellIcon } from "@heroicons/react/24/outline";
import { Loading } from "../../components/loading";
import { TeamLink } from "../../components/team-link";
import Label from "../../components/label";

interface TeamStatus {
  id: number;
  name: string;
  teamNumber: number | null;
  memberCount: number;
  maxMembers: number;
  hasHelmsman: boolean;
  hasBoat: boolean;
  isPaid: boolean;
  activeNotifications: {
    missing_members: boolean;
    missing_helmsman: boolean;
    missing_boat: boolean;
    payment_due: boolean;
  };
  status: {
    missing_members: boolean;
    missing_helmsman: boolean;
    missing_boat: boolean;
    payment_due: boolean;
  };
}

type NotificationType =
  | "missing_members"
  | "missing_helmsman"
  | "missing_boat"
  | "payment_due";

interface NotificationModalState {
  isOpen: boolean;
  teamId: number | null;
  teamName: string;
  notificationType: NotificationType | null;
  isAlreadyActive: boolean;
}

const notificationTypeLabels: Record<NotificationType, string> = {
  missing_members: "miembros faltantes",
  missing_helmsman: "timonel faltante",
  missing_boat: "embarcación faltante",
  payment_due: "pago pendiente",
};

export function TeamsNotificationsPage() {
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [teamsStatus, setTeamsStatus] = useState<TeamStatus[]>([]);
  const [notificationModal, setNotificationModal] =
    useState<NotificationModalState>({
      isOpen: false,
      teamId: null,
      teamName: "",
      notificationType: null,
      isAlreadyActive: false,
    });

  const {
    paginatedData: paginatedTeams,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setCurrentPage,
    setSelectedFilters,
  } = useTableControls<TeamStatus>(teamsStatus);

  const loadTeamsStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await TeamsAPI.getTeamsStatus();
      if (response.statusCode === 200) {
        setTeamsStatus(response.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSendNotification = useCallback(
    async (team: TeamStatus, notificationType: NotificationType) => {
      try {
        const isAlreadyActive = team.activeNotifications[notificationType];
        if (isAlreadyActive) {
          setNotificationModal({
            isOpen: true,
            teamId: team.id,
            teamName: team.name,
            notificationType,
            isAlreadyActive,
          });
          return;
        }
        const response = await UserNotificationsAPI.sendTeamNotification(
          team.id,
          notificationType
        );
        if (response.statusCode === 200 || response.statusCode === 201) {
          addToast({
            message: response.message || "Notificación enviada exitosamente",
            type: "success",
            location: "bottom-center",
          });
          loadTeamsStatus();
        } else {
          addToast({
            message: response.message || "Error al enviar la notificación",
            type: "error",
            location: "bottom-center",
          });
        }
      } catch (error) {
        addToast({
          message: "Error al enviar la notificación",
          type: "error",
          location: "bottom-center",
        });
      }
    },
    [addToast, loadTeamsStatus]
  );

  const handleConfirmSendNotification = useCallback(async () => {
    if (!notificationModal.teamId || !notificationModal.notificationType)
      return;

    setIsLoading(true);
    try {
      const response = await UserNotificationsAPI.sendTeamNotification(
        notificationModal.teamId,
        notificationModal.notificationType
      );

      if (response.statusCode === 200 || response.statusCode === 201) {
        addToast({
          message: response.message || "Notificación enviada exitosamente",
          type: "success",
          location: "bottom-center",
        });

        loadTeamsStatus();
      } else {
        addToast({
          message: response.message || "Error al enviar la notificación",
          type: "error",
          location: "bottom-center",
        });
      }
    } catch (error) {
      addToast({
        message: "Error al enviar la notificación",
        type: "error",
        location: "bottom-center",
      });
    } finally {
      setIsLoading(false);
      setNotificationModal((prev) => ({ ...prev, isOpen: false }));
    }
  }, [
    notificationModal.teamId,
    notificationModal.notificationType,
    addToast,
    loadTeamsStatus,
  ]);

  const handleCloseModal = useCallback(() => {
    setNotificationModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
    },
    [setSearchQuery]
  );

  const handleSort = useCallback(
    (sortBy: string | null, sortDirection: "asc" | "desc" | null) => {
      setSortConfig({
        sortBy: sortBy as keyof TeamStatus | null,
        sortDirection,
      });
    },
    [setSortConfig]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const handleFilterChange = useCallback(
    (selectedFilters: Record<string, string[]>) => {
      setSelectedFilters(selectedFilters);
    },
    [setSelectedFilters]
  );

  useEffect(() => {
    loadTeamsStatus();
  }, [loadTeamsStatus]);

  const columns = [
    {
      key: "name",
      label: "Equipo",
      sortable: true,
      render: (value: any, row: TeamStatus) => {
        return <TeamLink teamName={row.name} teamId={row.id} />;
      },
    },
    {
      key: "status.missing_members",
      label: "Miembros Faltantes",
      sortable: false,
      render: (value: any, row: TeamStatus) => {
        if (row.status.missing_members) {
          return (
            <NotificationButton
              onClick={() => handleSendNotification(row, "missing_members")}
            />
          );
        } else {
          return <Label color="green"> Completado </Label>;
        }
      },
    },
    {
      key: "status.missing_helmsman",
      label: "Timonel Faltante",
      sortable: false,
      render: (value: any, row: TeamStatus) => {
        if (row.status.missing_helmsman) {
          return (
            <NotificationButton
              onClick={() => handleSendNotification(row, "missing_helmsman")}
            />
          );
        } else {
          return <Label color="green"> Completado </Label>;
        }
      },
    },
    {
      key: "status.missing_boat",
      label: "Embarcación Faltante",
      sortable: false,
      render: (value: any, row: TeamStatus) => {
        if (row.status.missing_boat) {
          return (
            <NotificationButton
              onClick={() => handleSendNotification(row, "missing_boat")}
            />
          );
        } else {
          return <Label color="green"> Completado </Label>;
        }
      },
    },
    {
      key: "status.payment_due",
      label: "Pago Pendiente",
      sortable: false,
      render: (value: any, row: TeamStatus) => {
        if (row.status.payment_due) {
          return (
            <NotificationButton
              onClick={() => handleSendNotification(row, "payment_due")}
            />
          );
        } else {
          return <Label color="green"> Completado </Label>;
        }
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <CrudTableHeader
          title="Notificaciones por Equipo"
          count={teamsStatus.length}
          icon={<BellIcon className="w-6 h-6" />}
        />

        {isLoading ? (
          <Loading />
        ) : (
          <CrudTable
            columns={columns}
            data={paginatedTeams}
            search={{
              placeholder: "Buscar equipos...",
              onSearch: handleSearch,
            }}
            sort={{
              sortBy: sortConfig.sortBy,
              sortDirection: sortConfig.sortDirection,
              onSort: handleSort,
            }}
            pagination={{
              page: currentPage,
              totalPages: totalPages,
              onPageChange: handlePageChange,
              labels: {
                next: "Siguiente",
                previous: "Anterior",
                pageInfo: `Página ${currentPage} de ${totalPages}`,
              },
            }}
            filters={[
              {
                key: "status.missing_members",
                label: "Miembros Registrados",
                multiple: false,
                options: [
                  { key: "true", value: "Pendiente" },
                  { key: "false", value: "Completado" },
                ],
              },
              {
                key: "status.missing_helmsman",
                label: "Timonel Registrado",
                multiple: false,
                options: [
                  { key: "true", value: "Pendiente" },
                  { key: "false", value: "Completado" },
                ],
              },
              {
                key: "status.missing_boat",
                label: "Embarcación Registrada",
                multiple: false,
                options: [
                  { key: "true", value: "Pendiente" },
                  { key: "false", value: "Completado" },
                ],
              },
              {
                key: "status.payment_due",
                label: "Pago Realizado",
                multiple: false,
                options: [
                  { key: "true", value: "Pendiente" },
                  { key: "false", value: "Completado" },
                ],
              },
            ]}
            onFilterChange={handleFilterChange}
            totalCount={filteredData.length}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={notificationModal.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSendNotification}
        title="Confirmar envío de notificación"
        message={
          notificationModal.isAlreadyActive
            ? `¡Atención! El equipo "${
                notificationModal.teamName
              }" ya tiene una notificación activa de ${
                notificationModal.notificationType
                  ? notificationTypeLabels[notificationModal.notificationType]
                  : ""
              }. ¿Deseas enviar otra notificación?`
            : `¿Estás seguro de que deseas enviar una notificación de ${
                notificationModal.notificationType
                  ? notificationTypeLabels[notificationModal.notificationType]
                  : ""
              } al equipo "${notificationModal.teamName}"?`
        }
        confirmLabel="Enviar"
        cancelLabel="Cancelar"
      />
    </div>
  );
}

export const NotificationButton = ({
  onClick,
  label = "Enviar Notificación",
}: {
  onClick: () => void;
  label?: string;
}) => {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
    >
      {label}
    </button>
  );
};
